/**
 * googleAuthService — unified Google Sign-In.
 *
 * On Capacitor (iOS): uses @codetrix-studio/capacitor-google-auth (native SFSafariViewController).
 * On web: uses @react-oauth/google's useGoogleLogin hook (caller's responsibility).
 *
 * IMPORTANT: The plugin's load() is empty — GIDSignIn is only assigned inside
 * loadSignInClient(), called from initialize(). Must call initialize() before ANY
 * GoogleAuth operation or the native instance is nil and crashes on Plugin.swift.
 *
 * IMPORTANT: Never return the GoogleAuth plugin object from an async function and
 * then `await` the result — the plugin object is a Capacitor thenable proxy. When
 * JavaScript resolves a Promise that resolves to a thenable, it calls .then() on it,
 * which the native bridge doesn't implement → "GoogleAuth.then() is not implemented".
 * Keep all GoogleAuth calls inline within the same function that imported it.
 */

import { Capacitor } from '@capacitor/core';

const IOS_CLIENT_ID = '1024324880668-7a7d3c0edijo4kaqrdjqi45mul71lg6b.apps.googleusercontent.com';

function getGoogleAuthConfig() {
  const platform = Capacitor.getPlatform();
  const clientId = import.meta.env.REACT_APP_GOOGLE_CLIENT_ID;
  return {
    clientId: platform === 'ios' ? IOS_CLIENT_ID : clientId,
    scopes: ['profile', 'email'],
    grantOfflineAccess: false,
  };
}

export async function nativeGoogleSignIn() {
  const clientId = import.meta.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('Google Sign-In is not configured on this build.');

  const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
  await GoogleAuth.initialize(getGoogleAuthConfig());

  // Clear cached keychain tokens before signing in so GIDSignIn always goes
  // through Google's web flow and shows the account picker. This enables
  // account switching. The iOS "App wants to use accounts.google.com" system
  // dialog is tied to ASWebAuthenticationSession web cookies (not keychain
  // tokens), so it only appears once on first use — not on every sign-in.
  try { await GoogleAuth.signOut(); } catch (_) {}

  let user;
  try {
    user = await GoogleAuth.signIn();
  } catch (e) {
    console.error(`[GoogleSignIn] signIn failed: ${e?.message ?? e?.code ?? 'unknown error'}`);
    throw e;
  }

  const idToken = user?.authentication?.idToken;
  if (!idToken) throw new Error('Google Sign-In did not return an ID token');
  return idToken;
}

export async function nativeGoogleSignOut() {
  const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
  await GoogleAuth.initialize(getGoogleAuthConfig());
  await GoogleAuth.signOut();
}

export const isCapacitor = Capacitor.isNativePlatform();
